import { useMemo, useState } from "react";
import { Pause, Play, X, CaretDown, CaretRight } from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const BOTH_SHOPNOMIX = "shopnomix,shopnomix_kevin";
const keyOf = (b) => `${b.network}|${b.brand_id}`;
const DOMAIN_RX = /^(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\/\S*)?$/i;

/** One CSV line → cells (quoted fields, escaped quotes). */
function csvCells(line) {
  const cells = [];
  let field = "", quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") { cells.push(field); field = ""; }
    else field += c;
  }
  cells.push(field);
  return cells;
}

/**
 * Pasted text → [{name, domain}]. Accepts one brand name or domain per
 * line, or rows copied straight from a sheet (tab-separated) or CSV.
 * Header rows, section titles ("Apparel (218)") and notes are skipped.
 */
export function parseBrandList(text) {
  const out = [];
  for (const raw of (text || "").split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const cells = (line.includes("\t") ? line.split("\t") : csvCells(line))
      .map((c) => c.trim()).filter(Boolean);
    const lower = cells.map((c) => c.toLowerCase());
    if (lower.includes("brand") && lower.includes("domain")) continue;
    const domain = cells.find((c) => DOMAIN_RX.test(c) && !c.includes(" ")) || "";
    const name = cells.find((c) => c !== domain) || "";
    if (!domain && (/\(\d+\)$/.test(line) || line.length > 80)) continue;
    if (name || domain) out.push({ name, domain });
  }
  return out;
}

/**
 * Pause a list of brands for every publisher they are assigned to.
 * 1) pick the network, 2) paste the brand list, 3) Load — each brand is
 * shown with its publishers, 4) Pause all brands. Resume all lifts only
 * pauses made here (paused_reason=manual_brand_wide), never a pause an
 * admin set on one publisher.
 */
export default function BrandWidePause({ networks: allNetworks }) {
  const [network, setNetwork] = useState(BOTH_SHOPNOMIX);
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(null); // {rows, matched_rows, brands, unmatched}
  const [removed, setRemoved] = useState(() => new Set());
  const [open, setOpen] = useState(() => new Set());
  const [preview, setPreview] = useState(null);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const parsed = useMemo(() => parseBrandList(text), [text]);
  const brands = useMemo(
    () => (loaded?.brands || []).filter((b) => !removed.has(keyOf(b))),
    [loaded, removed],
  );
  const totals = useMemo(() => {
    const pubs = new Set();
    let live = 0, paused = 0;
    brands.forEach((b) => {
      (b.publishers || []).forEach((p) => pubs.add(p.publisher_id));
      live += b.assignments_active || 0;
      paused += b.assignments_paused || 0;
    });
    return { publishers: pubs.size, live, paused };
  }, [brands]);

  const load = async () => {
    if (!parsed.length) {
      toast.error("Paste at least one brand");
      return;
    }
    setBusy(true);
    try {
      const { data } = await API.post("/admin/pause-center/brands/match", {
        networks: network.split(","), rows: parsed, include_publishers: true,
      });
      setLoaded(data);
      setRemoved(new Set());
      setOpen(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load brands");
    } finally {
      setBusy(false);
    }
  };

  const body = (status, extra) => ({
    status,
    brands: brands.map((b) => ({ network: b.network, brand_id: b.brand_id })),
    ...extra,
  });

  const runPreview = async (status) => {
    setBusy(true);
    try {
      // Preview is read-only, so it is not held to the write cap; the real
      // write below is capped at exactly the count admin confirmed.
      const { data } = await API.post("/admin/publisher-brands/bulk-status",
        body(status, { dry_run: true, max_matches: 1000000 }));
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/admin/publisher-brands/bulk-status", body(preview.status, {
        dry_run: false,
        notify: notify ? "summary" : "none",
        // The server's cap pre-check counts before routed assignments are
        // excluded, so cap at that count, not the smaller `matched`.
        max_matches: Math.max(1, preview.candidates ?? preview.matched),
      }));
      toast.success(`${data.modified.toLocaleString()} assignments ${preview.status === "paused" ? "paused" : "resumed"} across ${data.publishers} publishers`);
      if (data.partial) toast.error(`Some writes failed: ${(data.write_errors || [])[0] || ""}`);
      setPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleOpen = (b) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(keyOf(b))) next.delete(keyOf(b)); else next.add(keyOf(b));
    return next;
  });

  return (
    <div data-testid="brand-wide-pause">
      <div className="grid gap-3 md:grid-cols-[220px_1fr] mb-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">1. Network</label>
          <select
            value={network}
            onChange={(e) => { setNetwork(e.target.value); setLoaded(null); }}
            className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white font-mono"
            data-testid="brand-wide-network"
          >
            <option value={BOTH_SHOPNOMIX}>shopnomix + shopnomix_kevin</option>
            {allNetworks.filter((n) => n !== "all").map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">
            2. Paste brands <span className="font-normal text-slate-400">— one per line: name, domain, or rows copied from the sheet</span>
          </label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder={"OLAPLEX\nadidas.com\n32 Degrees\t32degrees.com\tUS"}
            className="font-mono text-xs"
            data-testid="brand-wide-paste"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button className="h-9" disabled={busy || !parsed.length} onClick={load} data-testid="brand-wide-load">
              3. Load brands
            </Button>
            <span className="text-xs text-slate-500">{parsed.length} brands in the list</span>
          </div>
        </div>
      </div>

      {loaded && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-2 p-3 border border-slate-200 rounded-md bg-slate-50" data-testid="brand-wide-summary">
            <span className="text-sm">
              <b>{brands.length}</b> brands found ({loaded.matched_rows}/{loaded.rows} list rows) ·{" "}
              <b>{totals.publishers}</b> publishers · <b>{totals.live.toLocaleString()}</b> live ·{" "}
              {totals.paused.toLocaleString()} paused
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-9" disabled={busy || !brands.length}
                onClick={() => runPreview("active")} data-testid="brand-wide-resume">
                <Play size={14} className="mr-1" /> Resume all brands
              </Button>
              <Button size="sm" className="h-9 bg-amber-600 hover:bg-amber-700" disabled={busy || !brands.length}
                onClick={() => runPreview("paused")} data-testid="brand-wide-pause-btn">
                <Pause size={14} className="mr-1" /> Pause all brands
              </Button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Brand", "Feed", "Geo", "Publishers", "Live", "Paused", ""].map((h, i) => (
                    <th key={i} className={`${i >= 4 && i <= 5 ? "text-right" : "text-left"} px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-bold`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {brands.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">
                    None of the pasted brands are on {network.replace(",", " / ")}.
                  </td></tr>
                ) : brands.map((b) => {
                  const pubs = b.publishers || [];
                  const isOpen = open.has(keyOf(b));
                  return (
                    <tr key={keyOf(b)} className="align-top" data-testid={`brand-wide-row-${b.brand_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{b.name || "—"}</div>
                        <div className="text-xs text-slate-400 font-mono">{b.domain} · {b.brand_id}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{b.network}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{b.country_code || "—"}</td>
                      <td className="px-4 py-3">
                        {pubs.length === 0 ? (
                          <span className="text-xs text-slate-400">not assigned</span>
                        ) : (
                          <>
                            <button type="button" onClick={() => toggleOpen(b)}
                              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
                              {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                              {pubs.length} publisher{pubs.length === 1 ? "" : "s"}
                            </button>
                            {isOpen && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {pubs.map((p) => (
                                  <span key={p.publisher_id} title={p.email}
                                    className={`px-1.5 py-0.5 rounded text-[11px] font-mono ${
                                      p.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                                    }`}>
                                    {p.sub_id || p.email || p.publisher_id} · {p.status}
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">{b.assignments_active}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-amber-700">{b.assignments_paused}</td>
                      <td className="px-2 py-3 text-right">
                        <button type="button" title="Leave this brand out"
                          onClick={() => setRemoved((prev) => new Set(prev).add(keyOf(b)))}
                          className="text-slate-300 hover:text-red-600" data-testid={`brand-wide-remove-${b.brand_id}`}>
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {loaded.unmatched?.length > 0 && (
            <details className="mt-3 text-xs text-slate-500" data-testid="brand-wide-unmatched">
              <summary className="cursor-pointer">
                {loaded.unmatched.length} pasted brands not found on {network.replace(",", " / ")}
              </summary>
              <div className="mt-2 max-h-48 overflow-auto font-mono columns-2 md:columns-3">
                {loaded.unmatched.map((r, i) => <div key={i}>{r.name}{r.domain ? ` · ${r.domain}` : ""}</div>)}
              </div>
            </details>
          )}
        </>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && !busy && setPreview(null)}>
        <DialogContent data-testid="brand-wide-confirm">
          <DialogHeader>
            <DialogTitle>
              {preview?.status === "paused" ? "Pause" : "Resume"} {brands.length} brands for all their publishers?
            </DialogTitle>
            <DialogDescription>
              {preview?.status === "paused"
                ? "Paused assignments stop serving: the tracker blocks clicks instead of redirecting. Brands routed to another network are left alone."
                : "Only pauses made from this panel are lifted. A brand an admin paused for one publisher stays paused."}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="text-sm space-y-1">
              <div><b>{preview.matched.toLocaleString()}</b> assignments across <b>{preview.publishers}</b> publishers</div>
              <div className="text-xs text-slate-500 font-mono">{(preview.networks || []).join(", ")}</div>
              {preview.matched > 0 && (
                <label className="flex items-center gap-2 pt-2 text-sm">
                  <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                  Send each publisher one summary notification
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" disabled={busy} onClick={() => setPreview(null)}>Cancel</Button>
            <Button
              disabled={busy || !preview?.matched}
              className={preview?.status === "paused" ? "bg-amber-600 hover:bg-amber-700" : ""}
              onClick={confirm}
              data-testid="brand-wide-confirm-btn"
            >
              {preview?.status === "paused" ? "Pause" : "Resume"} {preview?.matched?.toLocaleString()} assignments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
